import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/api'
import { ApiError } from '@/api/client'
import type {
  AdminOverview,
  AdminUser,
  AuthUser,
  CoinLedgerItem,
  UpstreamCatalog,
  UpstreamCatalogResult,
  UpstreamCreate,
  UpstreamFamily,
  UpstreamItem,
} from '@/api'
import { useAdminAuth } from '@/context/AdminAuthContext'
import { useToast } from '@/context/ToastContext'
import { Icon } from '@/components/icons'
import { LedgerTable } from '@/components/LedgerTable'
import { Badge, Button, Card, CardBody, CardHead, Field, Input, Modal, Select, Stat } from '@/components/ui'

/**
 * 后台管理（`/admin`，**只有后台会话能到达**）。
 *
 * 三块：
 *   ① **上游通道** —— 按产出类型分组的「地址 / Token / 模型名」，改完立即生效；
 *   ② **账号与额度** —— **会员**账号：建号、改额度、改密码、停用；
 *   ③ **后台账号** —— 能进这个页面的人自己的账号（与 ② 是两张表）。
 *
 * ### 🔴 这里**没有**「需要管理员权限」那个分支了（2026-10-07 拆表）
 *
 * 拆表前「是不是管理员」是会员表上的一个字段，所以「一个会员敲了 `/admin`」
 * 是**真会发生**的状态，本页得给一段解释 + 一颗「改用管理员账号登录」的按钮。
 *
 * 拆表后这件事**在路由层就结束了**：`App.AdminRoute` 只看后台会话，
 * 没登就渲染后台登录页 —— 会员账号在后台登录入口那一侧「不存在」
 * （后端 `admin_accounts.login` 只查 `admins` 表）。于是本组件只在
 * **后台会话已建立**时被渲染，那个分支不可达。
 *
 * 留着它的坏处不是「多几行」：读代码的人会以为存在第四种状态
 * （登着会员、又进了后台正文），然后照着它写新逻辑。所以整块删掉，
 * 并把 `useAuth()` 也一并移出本文件 —— 后台这一页**一个会员字段都不该读**。
 * 真闸门仍在后端（`/api/admin/*` 全部 `require_admin`）。
 *
 * ### 三条要守住的口径
 *
 * 1. **Token 只以脱敏串出现**。后端只下发 `abcd••••••wxyz`，想换就整串重填。
 *    组件里**不持有完整 Key**，所以任何一次报错、截图、或 React DevTools 都不会
 *    把它带出去。留空提交 = 不动这一项；回传脱敏串 = 不动（后端认得那个圆点）。
 * 2. **「已自定义」必须标出来**。改了地址但界面上看不出来，运维会以为
 *    线上跑的还是默认值 —— 排查时先去查一个早就被改掉的默认地址。
 * 3. **额度是「设为某个值」，不是增量**。管理员在输入框里填的就是目标额度，
 *    显示成 `+500` 之类的增量口径，会出现「我明明填了 100，怎么变成 1100」。
 * 4. **后台账号不参与计费** —— 建号表单里**没有额度字段**，列表里也没有额度列。
 *    这不是「暂时没做」：`admins` 表里根本没有 `coins` 这一列，
 *    摆一个额度输入框出来只会让人以为填了有用。
 */
export function Admin() {
  return (
    <div className="page page--wide">
      <AdminOverviewBar />
      <UpstreamPanel />
      <UsersPanel />
      <AdminsPanel />
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/* 概览                                                                    */
/* ---------------------------------------------------------------------- */

function AdminOverviewBar() {
  const [data, setData] = useState<AdminOverview | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    api
      .adminOverview()
      .then(setData)
      .catch((e) => setErr(e instanceof ApiError ? e.message : '读取失败'))
  }, [])

  return (
    <Card>
      <CardHead title="总览" icon="chart" hint={err || '账号与用量的一眼印象'} />
      <CardBody>
        {data ? (
          <div className="admin__stats">
            <Stat label="会员账号" value={data.users} icon="users" delta={`${data.activeUsers} 个在用`} />
            {/* 后台账号**单独一格**，不与会员相加 —— 两张表是两个概念，
                加起来得到的数字没有任何人能用它回答一个问题。 */}
            <Stat label="后台账号" value={data.admins} icon="shield" delta="可进 /admin" />
            <Stat label="额度总额" value={data.totalCoins.toFixed(0)} suffix="积分" icon="coin" />
            <Stat label="已消耗" value={data.spentCoins.toFixed(0)} suffix="积分" icon="bolt" />
            <Stat label="任务" value={data.counts.jobs ?? 0} icon="activity" delta={`作品 ${data.counts.works ?? 0}`} />
          </div>
        ) : (
          <div className="row text-sm muted" style={{ gap: 8 }}>
            <span className="spinner" /> 正在读取…
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/* ---------------------------------------------------------------------- */
/* 上游通道                                                                */
/* ---------------------------------------------------------------------- */

/**
 * 上游通道。
 *
 * ### 2026-10-07：从「只能改」扩成「可增删 + 可切换」
 *
 * 用户口径：「如果我想保留默认，但是也可以在后台管理进行配置呢」。
 * 后端那边把「一条通道」拆成了 **家族（代码常量）+ 通道实例（数据）**，
 * 见 `runtime_config` 的模块 docstring。这一页要守住三条：
 *
 * 1. 🔴 **同一家族同时只有一条生效**。这不是产品选择，是物理约束：skill 模块在
 *    进程里只有一份，凭据落在模块属性/环境变量上，两条并存必然互相覆盖。
 *    所以「新增通道」= **备一份**，「启用」才真的切过去。这句话必须写在界面上 ——
 *    只写在代码注释里的话，管理员会把「加了两条」理解成「有了两条链路」。
 * 2. 🔴 **不显示字段能力以外的东西**。一行渲染哪几个输入框仍然由接口下发的
 *    `fields` 决定（与改造前一致）；新增的通道继承家族的 `fields`，
 *    所以「图片通道不能填模型名」这条口径没变。
 * 3. **改动接口都回整份清单**，所以这里拿返回值刷界面，不补 GET ——
 *    启用态是同族互斥的，改一条会影响另一条的显示，分两次读会出现错位。
 */
function UpstreamPanel() {
  const { success, error: showError } = useToast()
  const [catalog, setCatalog] = useState<UpstreamCatalog | null>(null)
  const [err, setErr] = useState('')
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(() => {
    api
      .listUpstreams()
      .then((c) => {
        setCatalog(c)
        setErr('')
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : '读取失败'))
  }, [])

  useEffect(reload, [reload])

  /** 调一个改动接口，成功后用**返回的整份清单**刷界面。 */
  const run = useCallback(
    async (fn: () => Promise<UpstreamCatalogResult>, msg: string) => {
      setBusy(true)
      try {
        const r = await fn()
        setCatalog({ groups: r.groups, families: r.families })
        setErr('')
        success(msg)
      } catch (e) {
        showError(e instanceof ApiError ? e.message : '操作失败')
      } finally {
        setBusy(false)
      }
    },
    [showError, success],
  )

  /**
   * 去重后的通道清单。
   *
   * ⚠️ **必须按 id 去重**：一个家族可以同时服务图与视频（网关就是），于是它会
   * 出现在两个分组里 —— 那是**同一条记录**（改一处两处都变）。不去重的话
   * 「这个家族还剩几条通道」会被数成两倍，而那个数字决定「删除」按钮能不能点。
   */
  const all = useMemo(() => {
    const seen = new Set<string>()
    const out: UpstreamItem[] = []
    for (const g of catalog?.groups ?? []) {
      for (const i of g.items) {
        if (seen.has(i.id)) continue
        seen.add(i.id)
        out.push(i)
      }
    }
    return out
  }, [catalog])

  const countByFamily = useMemo(() => {
    const m: Record<string, number> = {}
    for (const i of all) m[i.family] = (m[i.family] ?? 0) + 1
    return m
  }, [all])

  const customCount = all.filter((i) => !i.builtin).length

  /**
   * 「通道清单被动过」的判据 —— 决定「恢复默认通道」按钮出不出现。
   *
   * 🔴 不能只看 `customCount`。**删掉一条默认通道**时 `customCount` 仍然是 0
   * （一条都没加过），可那种情况下「恢复默认」是**唯一**的找回路径 ——
   * 按钮藏起来就等于「删掉的默认行再也回不来」，而用户点删除时并没有被这样告知。
   *
   * 判据第二条：某个家族一条 `builtin` 都不剩 —— 说明它的默认行被删掉了。
   */
  const channelsTouched = useMemo(() => {
    if (customCount > 0) return true
    if (!catalog) return false
    return catalog.families.some((f) => !all.some((i) => i.family === f.family && i.builtin))
  }, [all, catalog, customCount])

  /*
   * ⚠️ 这个面板里两处文案有个共同的坑：`CardHead` 的 `hint` 与 `window.confirm`
   * 都是**原样渲染**的，不认 markdown —— 写 `**立即生效**` 会真的显示成两颗星号。
   * 要加粗只能写 `<strong>`（confirm 里没有富文本，就只能不强调）。
   */
  return (
    <Card>
      <CardHead
        title="上游通道"
        icon="bolt"
        hint={
          <>
            每个产出类型下各条通道的 API 地址与 Token。保存 / 启用切换后
            <strong>立即生效</strong>（在跑的旧任务不受影响）
          </>
        }
        right={
          <div className="row" style={{ gap: 8 }}>
            <Button variant="ghost" size="sm" icon="refresh" onClick={reload} disabled={busy}>
              刷新
            </Button>
            {channelsTouched ? (
              <Button
                variant="ghost"
                size="sm"
                icon="refresh"
                disabled={busy || !catalog}
                onClick={() => {
                  // ⚠️ `window.confirm` 是纯文本：这里写 `**新增**` 会原样显示星号。
                  if (
                    !window.confirm(
                      '恢复默认通道清单？\n\n' +
                        '你自己新增的通道会被删掉，被删掉的默认通道会回来，启用态回到默认。\n' +
                        '已经填好的地址与 Token 不受影响。',
                    )
                  )
                    return
                  void run(() => api.resetUpstreamChannels(), '已恢复默认通道清单')
                }}
              >
                恢复默认通道
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              icon="plus"
              disabled={busy || !catalog}
              onClick={() => setAdding(true)}
            >
              添加通道
            </Button>
          </div>
        }
      />
      <CardBody>
        {err ? <div className="admin__error">{err}</div> : null}
        <div className="field__hint" style={{ marginBottom: 12 }}>
          同一个适配器家族下可以有<strong>多条通道</strong>（比如两个 duomi 账号），
          但<strong>同时只有一条生效</strong> —— 凭据在进程里只能有一套，所以新增的通道
          是「备一份」，要点「启用」才真的切过去。
          {customCount > 0 ? ` 当前有 ${customCount} 条是你自己加的。` : ''}
        </div>
        {catalog ? (
          <div className="stack">
            {catalog.groups.map((g) => (
              <div key={g.kind} className="admin__group">
                <div className="admin__group-head">
                  <span className="admin__group-label">{g.label}</span>
                  <span className="text-xs muted">{g.sub}</span>
                </div>
                <div className="stack stack--sm">
                  {g.items.map((item) => (
                    <UpstreamRow
                      key={item.id}
                      item={item}
                      siblingCount={countByFamily[item.family] ?? 1}
                      busy={busy}
                      onSaved={(msg) => {
                        success(msg)
                        reload()
                      }}
                      onActivate={() =>
                        void run(
                          () => api.activateUpstream(item.family, item.id),
                          `${item.label}：已启用`,
                        )
                      }
                      onDelete={() => {
                        if (
                          !window.confirm(
                            `删除通道「${item.label}」？\n\n` +
                              `它填过的地址与 Token 会一并清掉。` +
                              (item.active
                                ? `\n它现在是启用中的，删掉后这个家族的启用态会自动落到剩下的第一条。`
                                : ''),
                          )
                        )
                          return
                        void run(() => api.deleteUpstream(item.id), `已删除「${item.label}」`)
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row text-sm muted" style={{ gap: 8 }}>
            <span className="spinner" /> 正在读取上游清单…
          </div>
        )}
      </CardBody>

      {adding && catalog ? (
        <CreateChannelModal
          families={catalog.families}
          busy={busy}
          onClose={() => setAdding(false)}
          onCreate={(req) => {
            void run(() => api.createUpstream(req), `已新增通道「${req.label}」，点「启用」才会生效`)
            setAdding(false)
          }}
        />
      ) : null}
    </Card>
  )
}

/**
 * 添加通道。
 *
 * `family` 只能是接口下发过的家族 —— **不是自由文本**。因为一条通道能跑起来靠的是
 * 家族背后那段真实代码（skill 脚本 + `build_adapter()` 的分支），允许填一个不存在的
 * 家族，产出的就是「界面能配、点下去 `UPSTREAM_FAILED`」的假入口。
 */
function CreateChannelModal({
  families,
  busy,
  onClose,
  onCreate,
}: {
  families: UpstreamFamily[]
  busy: boolean
  onClose: () => void
  onCreate: (req: UpstreamCreate) => void
}) {
  const [family, setFamily] = useState(families[0]?.family ?? '')
  const [label, setLabel] = useState('')
  const [price, setPrice] = useState('')
  const [unit, setUnit] = useState('')

  const fam = families.find((f) => f.family === family)
  // 报价要么两个都填，要么都不填 —— 只填一个会落进 pricing 的兜底口径，
  // 账本上会出现读不通的记录（后端也会 400，这里先挡住少一次往返）。
  const priceTouched = price.trim() !== '' || unit.trim() !== ''
  const priceOk =
    !priceTouched || (price.trim() !== '' && unit.trim() !== '' && Number(price) > 0)
  const canSubmit = !!family && label.trim() !== '' && priceOk && !busy

  return (
    <Modal
      open
      onClose={onClose}
      title="添加上游通道"
      icon="bolt"
      width={540}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon="check"
            loading={busy}
            disabled={!canSubmit}
            onClick={() =>
              onCreate({
                family,
                label: label.trim(),
                ...(priceTouched ? { unitPrice: Number(price), unit: unit.trim() } : {}),
              })
            }
          >
            新增
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="适配器家族"
          required
          hint="你要复用哪条链路的代码。新供应商需要先写适配器（那是发版动作），这里配不出来。"
        >
          <Select value={family} onChange={(e) => setFamily(e.target.value)}>
            {families.map((f) => (
              <option key={f.family} value={f.family}>
                {f.label}（{f.family}）
              </option>
            ))}
          </Select>
        </Field>
        {fam ? <div className="field__hint" style={{ marginTop: -4 }}>{fam.note}</div> : null}
        <Field label="通道名称" required hint="只给你自己看，比如「duomi 备用账号」「网关测试地址」">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={fam ? `${fam.label} 备用` : '新通道'}
            autoFocus
          />
        </Field>
        <div className="grid-2">
          <Field label="报价单价" hint="不填就沿用价目表的默认口径">
            <Input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
              placeholder="0.10"
            />
          </Field>
          <Field label="单位" hint={fam?.unit ? `这个家族常用「${fam.unit}」` : '张 / 秒 / 条 / 次'}>
            <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder={fam?.unit || '张'} />
          </Field>
        </div>
        {!priceOk ? (
          <div className="admin__error">报价要么单价与单位一起填（单价需大于 0），要么都不填。</div>
        ) : null}
        <div className="field__hint">
          新增的通道<strong>不会立刻生效</strong> —— 保存后在列表里点「启用」才会切过去。
          {fam?.fields.includes('model')
            ? ''
            : ' 这一族只认地址与 Token，模型名挂在档位上（每个模型自己带），不在这条通道上。'}
        </div>
      </div>
    </Modal>
  )
}

/** 一个通道一行。草稿在这里、提交也在这里 —— 父组件不持有草稿。 */
function UpstreamRow({
  item,
  siblingCount,
  busy,
  onSaved,
  onActivate,
  onDelete,
}: {
  item: UpstreamItem
  /** 同家族共有几条通道（含自己）。=1 时不许删 —— 那是这个家族唯一的生效凭据 */
  siblingCount: number
  busy: boolean
  onSaved: (msg: string) => void
  onActivate: () => void
  onDelete: () => void
}) {
  const { success, error: showError } = useToast()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ baseUrl: item.baseUrl, model: item.model, apiKey: '' })
  /** 本地是「保存字段」在忙，与父组件的 `busy`（启用/删除）分开 —— 两件事不要共用一个 loading。 */
  const [saving, setSaving] = useState(false)

  // 重新拉回来（保存后 / 刷新）时**重置草稿**，否则输入框里还留着上一版文字，
  // 看起来像「没保存成功」。apiKey 草稿一并清空：它是「新填的 Key」，
  // 不是一个可以回填的值。
  useEffect(() => {
    setDraft({ baseUrl: item.baseUrl, model: item.model, apiKey: '' })
  }, [item.baseUrl, item.model, item.id])

  const patchOf = useMemo(() => {
    const p: Record<string, string> = {}
    if (item.fields.includes('baseUrl') && draft.baseUrl !== item.baseUrl) p.baseUrl = draft.baseUrl
    if (item.fields.includes('model') && draft.model !== item.model) p.model = draft.model
    // 空串 = 没填新 Key = 不动。（真要清空 Key 走「恢复默认」那条路。）
    if (item.fields.includes('apiKey') && draft.apiKey.trim()) p.apiKey = draft.apiKey.trim()
    return p
  }, [draft, item])

  const dirty = Object.keys(patchOf).length > 0
  const lastOfFamily = siblingCount <= 1

  async function save(patch: Record<string, string>, tip: string) {
    setSaving(true)
    try {
      const res = await api.updateUpstream(item.id, patch)
      onSaved(res.changed.length ? `${item.label}：已更新 ${res.changed.join(' / ')}` : `${item.label}：${tip}`)
    } catch (e) {
      showError(e instanceof ApiError ? e.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin__up" data-open={open} data-channel={item.id} data-active={item.active}>
      <button type="button" className="admin__up-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Icon name="chevron" size={14} style={{ transform: open ? 'none' : 'rotate(-90deg)' }} />
        <span className="admin__up-label">{item.label}</span>
        {/*
          「启用中 / 未启用」放在最前 —— 这一页最重要的一件事就是「现在真正在跑的是哪份凭据」。
          同族互斥（一次只有一条启用），所以这两个 badge 必须一眼可见，不能藏进展开区。
        */}
        {item.active ? (
          <Badge tone="success" icon="check">
            启用中
          </Badge>
        ) : (
          <Badge tone="neutral">未启用</Badge>
        )}
        {item.builtin ? <Badge tone="neutral">内置</Badge> : null}
        {item.overridden.length ? <Badge tone="warning">已改过</Badge> : null}
        {item.builtinKey ? <Badge tone="info">脚本内置 Key</Badge> : null}
        {item.unitPrice != null ? (
          <Badge tone="neutral">{`¥${item.unitPrice}/${item.unit || '次'}`}</Badge>
        ) : null}
        {item.available ? null : <Badge tone="danger">家族已不存在</Badge>}
        <span className="text-xs muted admin__up-eff">{item.baseUrl || '（未设置）'}</span>
      </button>

      {open ? (
        <div className="admin__up-body">
          <div className="field__hint" style={{ marginBottom: 10 }}>
            {item.note}
            {item.builtinKey ? ' · 这一条脚本里自带 Key，Token 留空照样能用。' : ''}
          </div>

          <div className="grid-2">
            {item.fields.includes('baseUrl') ? (
              <Field label="接口地址">
                <Input
                  value={draft.baseUrl}
                  placeholder="https://..."
                  onChange={(e) => setDraft((d) => ({ ...d, baseUrl: e.target.value }))}
                />
              </Field>
            ) : null}
            {item.fields.includes('model') ? (
              <Field label="模型名" hint={item.modelHint || undefined}>
                <Input
                  value={draft.model}
                  placeholder={item.modelHint || '如 deepseek-flash'}
                  onChange={(e) => setDraft((d) => ({ ...d, model: e.target.value }))}
                />
              </Field>
            ) : null}
          </div>

          {item.fields.includes('apiKey') ? (
            <Field
              label="Token"
              hint={
                item.hasApiKey
                  ? `当前：${item.apiKeyMasked}。留空 = 不修改；想换就整串重填（读不回来是刻意的）。`
                  : '当前没有 Key。填上整串后保存即可。'
              }
            >
              <Input
                type="password"
                value={draft.apiKey}
                placeholder={item.hasApiKey ? item.apiKeyMasked || '••••••' : 'sk-...'}
                onChange={(e) => setDraft((d) => ({ ...d, apiKey: e.target.value }))}
                autoComplete="off"
              />
            </Field>
          ) : null}

          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Button variant="primary" size="sm" icon="check" loading={saving} disabled={!dirty} onClick={() => save(patchOf, '没有改动')}>
              保存
            </Button>
            {/*
              「恢复默认」= 把这一行的**所有**字段提交成空串（后端把空串理解为
              「清除这一项的自定义值」）。只清其中几个会让「改了地址没改 Key」
              这种半恢复状态留在库里 —— 而界面上那一行又会显示「默认」。
            */}
            <Button
              variant="outline"
              size="sm"
              icon="refresh"
              loading={saving}
              disabled={!item.overridden.length}
              onClick={() => {
                const all: Record<string, string> = {}
                for (const f of item.fields) all[f] = ''
                void save(all, '本来就是默认值')
              }}
            >
              恢复默认
            </Button>

            {/*
              「启用这条」只对**没启用**的行出现。切过去 = 写 active → apply
              → reset_adapters（后端一条龙），立刻生效、不用重启。
              也正因为同族互斥，启用一条会自动让同族另一条变成「未启用」。
            */}
            {item.active ? null : (
              <Button
                variant="outline"
                size="sm"
                icon="play"
                loading={busy}
                disabled={!item.available}
                onClick={onActivate}
              >
                启用这条
              </Button>
            )}

            {lastOfFamily ? null : (
              <Button variant="ghost" size="sm" icon="trash" disabled={busy} onClick={onDelete}>
                删除
              </Button>
            )}
          </div>

          {/*
            为什么「只剩一条时不给删」：删掉之后这个家族就没有生效凭据了，走它的任务
            会在构造适配器时失败，而界面上连一条可配置的通道都不剩。
            把原因写出来比只把按钮藏掉好 —— 否则用户会以为「界面出问题了」。
          */}
          {lastOfFamily ? (
            <div className="field__hint" style={{ marginTop: 8 }}>
              这是这个家族<strong>唯一</strong>的通道，不能删（删了就没有凭据可用了）。
              不想用它就把地址 / Token 改掉，或者先加一条别的再删它。
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/* ---------------------------------------------------------------------- */
/* 账号与额度                                                              */
/* ---------------------------------------------------------------------- */

/**
 * 会员账号（`users` 表）。
 *
 * ### 🔴 这里**不再有**「别把自己停用/删掉」的判断（2026-10-07 拆表）
 *
 * 拆表前管理员本人也是 `users` 里的一行，所以列表里会出现「当前账号」，
 * 并对它隐藏停用/删除按钮。现在管理员住在 `admins` 表，**它的 id 永远不可能
 * 出现在这个列表里** —— 那条判断恒为 false，是纯粹的死代码，
 * 而且它会让人以为「这两张表还有关系」。
 *
 * 自我保护的活口搬到了 `AdminsPanel`（那里才真有「自己」这个概念），
 * 后端也对应地挪了：`/api/admin/users/*` 上没有 `CANNOT_*_SELF`，
 * 那些码只长在 `/api/admin/admins/*` 上。
 */
function UsersPanel() {
  const { success, error: showError } = useToast()
  const [users, setUsers] = useState<AuthUser[] | null>(null)
  const [err, setErr] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AuthUser | null>(null)
  const [ledgerOf, setLedgerOf] = useState<AuthUser | null>(null)

  const reload = useCallback(() => {
    api
      .listUsers()
      .then((u) => {
        setUsers(u)
        setErr('')
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : '读取失败'))
  }, [])

  useEffect(reload, [reload])

  /** 停用 / 启用。停用会把该账号的**所有会话立即踢掉**（后端 set 了 status）。 */
  async function toggleStatus(u: AuthUser) {
    try {
      await api.updateUser(u.id, { status: u.status === 'active' ? 'disabled' : 'active' })
      success(u.status === 'active' ? `已停用 ${u.account}` : `已启用 ${u.account}`)
      reload()
    } catch (e) {
      showError(e instanceof ApiError ? e.message : '操作失败')
    }
  }

  async function removeUser(u: AuthUser) {
    // 二次确认走原生 confirm：这是一条**不可逆**的动作（账号没了），
    // 而且它只删账号、不删历史任务与账本（后端注释里有写）。
    if (!window.confirm(`确定删除会员账号 ${u.account}？\n\n它的任务、作品与额度账本会留在库里（账是审计凭据）。`)) return
    try {
      await api.deleteUser(u.id)
      success(`已删除 ${u.account}`)
      reload()
    } catch (e) {
      showError(e instanceof ApiError ? e.message : '删除失败')
    }
  }

  return (
    <Card>
      <CardHead
        title="会员账号"
        icon="users"
        hint="工作台的账号。额度是「设为某个值」，每一笔调整都会记进该账号的额度账本"
        right={
          <div className="row" style={{ gap: 8 }}>
            <Button variant="ghost" size="sm" icon="refresh" onClick={reload}>
              刷新
            </Button>
            <Button variant="primary" size="sm" icon="plus" onClick={() => setCreating(true)}>
              新建会员
            </Button>
          </div>
        }
      />
      <CardBody>
        {err ? <div className="admin__error">{err}</div> : null}
        {users ? (
          <div className="table__wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>账号</th>
                  <th>昵称</th>
                  <th>额度</th>
                  <th>套餐</th>
                  <th>状态</th>
                  <th style={{ width: 260 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="mono">{u.account}</td>
                    <td>{u.displayName}</td>
                    <td className="mono">{u.coins.toFixed(2)}</td>
                    <td className="text-xs muted">{u.plan}</td>
                    <td>{u.status === 'active' ? <Badge tone="success">正常</Badge> : <Badge tone="danger">已停用</Badge>}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <Button variant="ghost" size="sm" onClick={() => setEditing(u)}>
                          编辑
                        </Button>
                        <Button variant="ghost" size="sm" icon="coin" onClick={() => setLedgerOf(u)}>
                          账本
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(u)}>
                          {u.status === 'active' ? '停用' : '启用'}
                        </Button>
                        <Button variant="ghost" size="sm" className="admin__danger" onClick={() => removeUser(u)}>
                          删除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="row text-sm muted" style={{ gap: 8 }}>
            <span className="spinner" /> 正在读取账号…
          </div>
        )}
      </CardBody>

      {creating ? (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onDone={(acct) => {
            setCreating(false)
            success(`已创建 ${acct}`)
            reload()
          }}
        />
      ) : null}

      {editing ? (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onDone={(acct) => {
            setEditing(null)
            success(`已更新 ${acct}`)
            reload()
          }}
        />
      ) : null}

      {ledgerOf ? <LedgerModal user={ledgerOf} onClose={() => setLedgerOf(null)} /> : null}
    </Card>
  )
}

/**
 * 新建**会员**账号。
 *
 * ⚠️ 这里**没有角色选择**（2026-10-07 拆表）。建一个能进后台的账号要走
 * 下面的「后台账号」面板 —— 两张表、两个端点、两种字段集。
 * 表单里留一个「角色」下拉，只会让人以为在这里就能造管理员，
 * 而那个下拉一旦失效（后端不认 `role`），症状是「建出来的人进不了后台」，
 * 页面上一切正常。
 */
function CreateUserModal({ onClose, onDone }: { onClose: () => void; onDone: (account: string) => void }) {
  const [account, setAccount] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('123456')
  const [coins, setCoins] = useState('10000')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit() {
    setBusy(true)
    setErr('')
    try {
      const created = await api.createUser({
        account: account.trim(),
        password,
        displayName: displayName.trim() || undefined,
        coins: Number(coins) || 0,
      })
      onDone(created.account)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="新建会员"
      icon="users"
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <div className="grow" />
          <Button variant="primary" icon="check" loading={busy} disabled={!account.trim() || !password} onClick={submit}>
            创建
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="账号" required hint="登录工作台用的名字，可以是邮箱或短名（如 amazon@hsy.com）">
          <Input value={account} onChange={(e) => setAccount(e.target.value)} placeholder="someone@hsy.com" autoFocus />
        </Field>
        <Field label="昵称">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="留空则用账号" />
        </Field>
        <div className="grid-2">
          <Field label="初始额度" required hint="积分口径：1 元 = 10 积分">
            <Input value={coins} onChange={(e) => setCoins(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="初始密码" required>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        </div>
        {err ? <div className="admin__error">{err}</div> : null}
      </div>
    </Modal>
  )
}

function EditUserModal({
  user,
  onClose,
  onDone,
}: {
  user: AuthUser
  onClose: () => void
  onDone: (account: string) => void
}) {
  const [displayName, setDisplayName] = useState(user.displayName)
  const [plan, setPlan] = useState(user.plan)
  const [coins, setCoins] = useState(String(user.coins))
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit() {
    setBusy(true)
    setErr('')
    try {
      await api.updateUser(user.id, {
        displayName,
        plan,
        // 空串 = 不改密码（后端只在 `password` 非空时才改）
        password: password || undefined,
        coins: Number(coins),
      })
      onDone(user.account)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`编辑 ${user.account}`}
      icon="settings"
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <div className="grow" />
          <Button variant="primary" icon="check" loading={busy} onClick={submit}>
            保存
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="grid-2">
          <Field label="昵称">
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </Field>
          <Field label="套餐名">
            <Input value={plan} onChange={(e) => setPlan(e.target.value)} />
          </Field>
        </div>
        <Field
          label="额度"
          required
          hint="**设为这个值**（不是加减）。差额会写成一条账本记录，账本里查得到是谁调的。"
        >
          <Input value={coins} onChange={(e) => setCoins(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="重置密码" hint="留空 = 不改。改了之后该账号的**所有登录会被立刻踢掉**。">
          <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="留空不改" autoComplete="new-password" />
        </Field>
        {err ? <div className="admin__error">{err}</div> : null}
      </div>
    </Modal>
  )
}

/* ---------------------------------------------------------------------- */
/* 后台账号                                                                */
/* ---------------------------------------------------------------------- */

/**
 * 后台账号（`admins` 表）—— **能进这个页面的人自己的那张表**。
 *
 * ### 为什么它必须和「会员账号」并排出现在同一页
 *
 * 两张表是两个概念，但**同一个管理员**经常要在一次操作里同时想这两件事：
 * 「给 amazon@hsy.com 加额度」和「给小李开个后台号」。分成两个页面/两个入口，
 * 代价是每次都要先判断「这件事属于哪一页」。所以它们是同一页里的**两张卡**，
 * 而不是一个页面里的两个页签 —— 字段集不同、动作不同，没有共享的决策域。
 *
 * ### 三条自我保护**必须在前端也挡一次**
 *
 *   · 停用/删除自己 → 当场把自己锁在门外；
 *   · 删最后一个后台账号 → 谁也进不来了（后端有 `CANNOT_DELETE_LAST_ADMIN`）。
 *
 * 后端拦得住，但**只在点了之后才拦** —— 那时用户已经按完了、还挨了一句报错。
 * 所以这里直接不渲染那几个按钮，并写清楚为什么。
 * ⚠️ 前端这层是**体验**，不是闸门：真判据仍在后端（`require_admin` 那一段）。
 *
 * ### 没有额度、没有套餐
 *
 * 建号表单里只有账号 / 昵称 / 口令三项，列表里也没有额度列 ——
 * `admins` 表里根本没有那几列（见后端 `app/admin_accounts.py` 头部）。
 * 摆一个额度输入框出来，只会让人以为填了有用。
 */
function AdminsPanel() {
  const { admin: me } = useAdminAuth()
  const { success, error: showError } = useToast()
  const [admins, setAdmins] = useState<AdminUser[] | null>(null)
  const [err, setErr] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)

  const reload = useCallback(() => {
    api
      .listAdmins()
      .then((a) => {
        setAdmins(a)
        setErr('')
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : '读取失败'))
  }, [])

  useEffect(reload, [reload])

  const onlyOne = (admins?.length ?? 0) <= 1

  async function toggleStatus(a: AdminUser) {
    try {
      await api.updateAdmin(a.id, { status: a.status === 'active' ? 'disabled' : 'active' })
      success(a.status === 'active' ? `已停用 ${a.account}` : `已启用 ${a.account}`)
      reload()
    } catch (e) {
      showError(e instanceof ApiError ? e.message : '操作失败')
    }
  }

  async function removeAdmin(a: AdminUser) {
    if (!window.confirm(`确定删除后台账号 ${a.account}？\n\n他手上那个后台会话会被立刻吊销。`)) return
    try {
      await api.deleteAdmin(a.id)
      success(`已删除 ${a.account}`)
      reload()
    } catch (e) {
      showError(e instanceof ApiError ? e.message : '删除失败')
    }
  }

  return (
    <Card>
      <CardHead
        title="后台账号"
        icon="shield"
        hint="能进 /admin 的账号。与会员是两套身份 —— 这些账号不能跑生成任务，也没有额度"
        right={
          <div className="row" style={{ gap: 8 }}>
            <Button variant="ghost" size="sm" icon="refresh" onClick={reload}>
              刷新
            </Button>
            <Button variant="primary" size="sm" icon="plus" onClick={() => setCreating(true)}>
              新建后台账号
            </Button>
          </div>
        }
      />
      <CardBody>
        {err ? <div className="admin__error">{err}</div> : null}
        {admins ? (
          <div className="table__wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>账号</th>
                  <th>昵称</th>
                  <th>状态</th>
                  <th>最近登录</th>
                  <th style={{ width: 260 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => {
                  const self = a.id === me?.id
                  return (
                    <tr key={a.id} data-self={self}>
                      <td className="mono">{a.account}</td>
                      <td>{a.displayName}</td>
                      <td>
                        {a.status === 'active' ? <Badge tone="success">正常</Badge> : <Badge tone="danger">已停用</Badge>}
                      </td>
                      <td className="text-xs muted">{a.lastLoginAt || '从未登录'}</td>
                      <td>
                        <div className="row" style={{ gap: 6 }}>
                          <Button variant="ghost" size="sm" onClick={() => setEditing(a)}>
                            编辑
                          </Button>
                          {self ? (
                            <span className="text-xs muted">（当前登录的账号）</span>
                          ) : onlyOne ? (
                            // 唯一一个后台账号：停用或删掉它就没人能进后台了（后端也拦）
                            <span className="text-xs muted">（唯一后台账号，不可停用或删除）</span>
                          ) : (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => toggleStatus(a)}>
                                {a.status === 'active' ? '停用' : '启用'}
                              </Button>
                              <Button variant="ghost" size="sm" className="admin__danger" onClick={() => removeAdmin(a)}>
                                删除
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="row text-sm muted" style={{ gap: 8 }}>
            <span className="spinner" /> 正在读取后台账号…
          </div>
        )}
      </CardBody>

      {creating ? (
        <CreateAdminModal
          onClose={() => setCreating(false)}
          onDone={(acct) => {
            setCreating(false)
            success(`已创建后台账号 ${acct}`)
            reload()
          }}
        />
      ) : null}

      {editing ? (
        <EditAdminModal
          admin={editing}
          onClose={() => setEditing(null)}
          onDone={(acct) => {
            setEditing(null)
            success(`已更新 ${acct}`)
            reload()
          }}
        />
      ) : null}
    </Card>
  )
}

function CreateAdminModal({ onClose, onDone }: { onClose: () => void; onDone: (account: string) => void }) {
  const [account, setAccount] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit() {
    setBusy(true)
    setErr('')
    try {
      const created = await api.createAdmin({
        account: account.trim(),
        password,
        displayName: displayName.trim() || undefined,
      })
      onDone(created.account)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="新建后台账号"
      icon="shield"
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <div className="grow" />
          <Button variant="primary" icon="check" loading={busy} disabled={!account.trim() || !password} onClick={submit}>
            创建
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="账号"
          required
          hint="登录 /admin 用的名字。与会员账号是两张表，所以**可以和工作台某个会员重名**而不冲突。"
        >
          <Input value={account} onChange={(e) => setAccount(e.target.value)} placeholder="ops@hsy.com" autoFocus />
        </Field>
        <Field label="昵称">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="留空则用账号" />
        </Field>
        <Field label="初始密码" required hint="至少 6 位（后端强制）。口令在这里只看得见这一次。">
          <Input value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        {err ? <div className="admin__error">{err}</div> : null}
      </div>
    </Modal>
  )
}

function EditAdminModal({
  admin,
  onClose,
  onDone,
}: {
  admin: AdminUser
  onClose: () => void
  onDone: (account: string) => void
}) {
  const [displayName, setDisplayName] = useState(admin.displayName)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit() {
    setBusy(true)
    setErr('')
    try {
      await api.updateAdmin(admin.id, {
        displayName,
        // 空串 = 不改密码（后端只在 `password` 非空时才改）
        password: password || undefined,
      })
      onDone(admin.account)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`编辑 ${admin.account}`}
      icon="settings"
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <div className="grow" />
          <Button variant="primary" icon="check" loading={busy} onClick={submit}>
            保存
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="昵称">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="重置密码" hint="留空 = 不改。改了之后**这个后台账号的所有登录会被立刻踢掉**（工作台不受影响）。">
          <Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="留空不改"
            autoComplete="new-password"
          />
        </Field>
        {/* 没有额度/套餐字段 —— 后台身份不参与计费，`admins` 表里也没有那几列。
            停用/启用不在这里：它是列表里的一个动作，不是在编辑框里改的一个值。 */}
        {err ? <div className="admin__error">{err}</div> : null}
      </div>
    </Modal>
  )
}

/**
 * 额度账本弹层 —— **后台管理里看某个账号的账本**。
 *
 * ⚠️ 2026-10-07 起它**只服务于管理员看别人**。普通账号看自己那份已经升成整页
 * （`/account`，用户原话「点开可以新页面」），所以这里不再有「user 为空就看自己」
 * 的分支 —— 那条分支现在会渲染出和账号中心重复的第二份账本，且入口不在同一处。
 * 表格本体在 `components/LedgerTable.tsx`，两处共用一份实现。
 */
export function LedgerModal({ user, onClose }: { user: AuthUser; onClose: () => void }) {
  const [items, setItems] = useState<CoinLedgerItem[] | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    api
      .userLedger(user.id)
      .then(setItems)
      .catch((e) => setErr(e instanceof ApiError ? e.message : '读取失败'))
  }, [user])

  return (
    <Modal open onClose={onClose} title={`${user.account} · 额度账本`} icon="coin" width={680}>
      {err ? <div className="admin__error">{err}</div> : null}
      {items ? (
        <LedgerTable items={items} maxHeight="52vh" />
      ) : (
        <div className="row text-sm muted" style={{ gap: 8 }}>
          <span className="spinner" /> 正在读取账本…
        </div>
      )}
    </Modal>
  )
}
